import { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Grid } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

// Our Components
import Dropdown from 'components/Dropdown/Dropdown';
import FinancialGoalDropdown from 'components/Client/Onboarding/FinancialGoalDropdown';
import LiabilityFormHeading from 'components/Client/Onboarding/LiabilityFormHeading';
import LiabilityDynamicButton from 'components/Client/Onboarding/LiabilityDynamicButton';
import LoanTypeDropdown from 'components/Client/Onboarding/LoanTypeDropdown';
import Loader from 'components/Loader';
import MonthlyPaymentInput from 'components/Client/Onboarding/MonthlyPaymentInput';
import OutstandingBalanceInput from 'components/Client/Onboarding/OutstandingBalanceInput';
import RateInput from 'components/Input/RateInput';
import TextInput from 'components/Input/TextInput';
import StandardDatePicker from 'components/DatePicker/StandardDatePicker';
import TenureDropdown from 'components/Client/Onboarding/TenureDropdown';
import { NUMBER, TEXT } from 'components/Input/Types';

// Utils
import {
	dataIsValid,
	isSubmissionReady,
	roundLiabilityRate
} from 'shared/utils';
import sentenceCapitalize from 'shared/utils/formatting/sentenceCapitalize';

import dateToIsoFormat from 'shared/utils/clientOnboarding/dateToIsoFormat';
import shouldUpdateRateManuallyUpdated from 'shared/utils/events/shouldUpdateRateManuallyUpdated';
import FINANCIAL_GOAL_TO_ENUM_MAPPING from 'shared/utils/clientOnboarding/financialGoalMapping';
import getDefaultApproxPayoffDate from 'shared/utils/clientOnboarding/getDefaultApproxPayoffDate';
import isAutomatedRun from 'shared/utils/events/isAutomatedRun';

// Our Hooks
import useGetUserStudentLiability from 'hooks/clientOnboarding/useGetUserStudentLiability';
import useSaveStudentLiability from 'hooks/clientOnboarding/mutations/useSaveStudentLiability';
import usePrepopulateState from 'hooks/prepopulate/usePrepopulateState';
import useDisableOutstandingBalanceBasedOnSource from 'hooks/liability/formValidation/useDisableOutstandingBalanceBasedOnSource';

// CONSTANTS
import {
	DEGREE_TYPES,
	STUDENT,
	TYPES_OF_STUDENT_LOANS
} from 'shared/constants';

const GRADUATION_YEAR_ONLY_VIEW = ['year']; // This is for the DatePicker of Graduation Year

function StudentLiability() {
	// Student Liability Related
	const navigate = useNavigate();
	const { isLoading, data, isSuccess } = useGetUserStudentLiability();
	const saveStudentLiability = useSaveStudentLiability();

	const { isLoading: isMutationLoading } = saveStudentLiability;

	const [approxPayoffDate, setApproxPayoffDate] = useState(
		getDefaultApproxPayoffDate(STUDENT)
	);
	const [lender, setLender] = useState('');
	const [lenderType, setLenderType] = useState('Federal');
	const [loanType, setLoanType] = useState('Fixed');
	const [monthlyPayment, setMonthlyPayment] = useState('');
	const [ownershipTenure, setOwnershipTenure] = useState('');
	const [outstandingBalance, setOutstandingBalance] = useState(''); // balance / 100 is bc methodfi provides balance in cents so we divide by 100 to convert into dollars
	const [rate, setRate] = useState('');

	// Student Related Data
	const [fieldOfStudy, setFieldOfStudy] = useState('');
	const [gpa, setGpa] = useState('');
	const [graduationYear, setGraduationYear] = useState(new Date('2022'));
	const [highestDegree, setHighestDegree] = useState('Bachelors');
	const [schoolName, setSchoolName] = useState('NA');
	const [linkedinProfile, setLinkedinProfile] = useState('');

	// Financial Goal
	const [financialGoal, setFinancialGoal] = useState(
		'Reduce total cost of debt'
	);

	const disableOutstandingBalanceBasedOnSource =
		useDisableOutstandingBalanceBasedOnSource(data);

	const didRateChange = useMemo(() => {
		// if data is empty false
		if (!dataIsValid(data)) return false;
		// if interestRate is empty false.
		if (!dataIsValid(data?.interestRate)) return false;
		// if rate is empty false.
		if (rate === '') return false;
		// compare NOW
		return +rate !== data.interestRate;
	}, [rate, data]);

	usePrepopulateState(
		isSuccess
			? [
					{
						setState: setOutstandingBalance,
						value: `${data?.outstandingBalance ?? ''}`
					},
					{
						setState: setRate,
						value: `${
							data?.interestRate
								? roundLiabilityRate(data?.interestRate)
								: ''
						}`
					},
					{
						setState: setApproxPayoffDate,
						value: data?.expectedPayOffDate
					},
					{ setState: setLender, value: data?.lender },
					{ setState: setMonthlyPayment, value: data?.monthlyPay },
					{
						setState: setOwnershipTenure,
						value: `${Math.floor(data?.tenureMonth ?? 0) / 12}`
					},
					{
						setState: setLenderType,
						value: `${sentenceCapitalize(data?.subType ?? '')}`
					},
					{
						setState: setLoanType,
						value: `${sentenceCapitalize(
							data?.loanType ?? 'Fixed'
						)}`
					},
					{ setState: setFinancialGoal, value: data?.financialGoal },
					{
						setState: setFieldOfStudy,
						value: data?.studentInfo?.fieldOfStudy
					},
					{ setState: setGpa, value: data?.studentInfo?.gpa },
					{
						setState: setGraduationYear,
						value: data?.studentInfo?.graduationYear
					},
					{
						setState: setLinkedinProfile,
						value: data?.studentInfo?.linkedin
					},
					{
						setState: setHighestDegree,
						value: data?.studentInfo?.degree
					},
					{
						setState: setSchoolName,
						value: data?.studentInfo?.school
					}
			  ]
			: [],
		[data]
	);

	const formValues = [
		approxPayoffDate,
		financialGoal,
		graduationYear,
		highestDegree,
		lender,
		lenderType,
		loanType,
		monthlyPayment,
		outstandingBalance,
		ownershipTenure,
		rate,
		schoolName
	];

	const isFormReady = useMemo(
		() =>
			isAutomatedRun()
				? true
				: isSubmissionReady(formValues) &&
				  approxPayoffDate !== null &&
				  graduationYear !== null,
		formValues
	);

	const handleApproxPayoffDateChange = (value) => {
		const isValueEmpty = value === '';
		if (isValueEmpty) {
			setApproxPayoffDate('');
			return;
		}
		setApproxPayoffDate(value);
	};

	const handleGpachange = (value) => {
		const isValueEmpty = value === '';
		if (isValueEmpty) {
			setGpa(value);
			return;
		}
		const hasPointlessDecimal = value.includes('4.');
		if (hasPointlessDecimal) return;

		// if we made it here the value is not empty and its not 4.something
		const parsedValue = +value;
		const hasDecimal = parsedValue % 1 !== 0;

		if (parsedValue >= 1 && parsedValue <= 4) {
			if (hasDecimal) {
				const roundedValue = Math.floor(parsedValue * 100) / 100;

				setGpa(roundedValue);
				return;
			}

			setGpa(value);
		}
	};

	const paramsFromURL = useParams();
	const clientId = paramsFromURL?.clientId;

	const submitLiabilityData = (route) => {
		const formattedApproxPayOffDate = dateToIsoFormat(approxPayoffDate);
		const formattedFinancialGoal =
			FINANCIAL_GOAL_TO_ENUM_MAPPING(financialGoal);
		const formattedGraduationYear = new Date(graduationYear).getFullYear(); // graduationYear is an object but this turns it into a number
		const formattedGPA = +gpa; // must be a num
		const formattedRate = roundLiabilityRate(+rate);

		// everything below is subject to change once we databind.
		const studentLiability = {
			clientId,
			approxPayoffDate: formattedApproxPayOffDate,
			financialGoal: formattedFinancialGoal,
			fieldOfStudy,
			gpa: formattedGPA,
			graduationYear: formattedGraduationYear,
			degree: highestDegree,
			lender,
			lenderType: lenderType.toUpperCase(),
			linkedin: linkedinProfile,
			loanType: loanType.toUpperCase(),
			monthlyPayment: +monthlyPayment,
			outstandingBalance: +outstandingBalance,
			rate: formattedRate,
			school: schoolName,
			tenure: +ownershipTenure,
			tradelineType: STUDENT // can be one of AUTO | MORTGAGE | PERSONAL | STUDENT
		};

		studentLiability.rateManuallyUpdated = shouldUpdateRateManuallyUpdated(
			data?.rateManuallyUpdated,
			didRateChange
		);

		saveStudentLiability.mutate(studentLiability, {
			onSuccess: () => {
				navigate(route);
			}
		});
	};

	if (isLoading)
		return (
			<>
				<Helmet>
					<title>Student Loan</title>
				</Helmet>
				<LiabilityFormHeading headingText="Student Loan" />
				<Loader
					size={60}
					boxSX={{ alignItems: 'center', marginTop: 15 }}
				/>
			</>
		);

	return (
		<>
			<Helmet>
				<title>Student Loan</title>
			</Helmet>

			{/* Once data binding begins we will add Mortgage specific information to this header */}
			<LiabilityFormHeading headingText="Student Loan" />

			<Box component="form" noValidate autoComplete="off">
				<Grid container columnSpacing={2} marginTop={4}>
					<Grid item xs={6}>
						<OutstandingBalanceInput
							outstandingBalance={outstandingBalance}
							setOutstandingBalance={setOutstandingBalance}
							disabled={disableOutstandingBalanceBasedOnSource}
						/>
					</Grid>
					<Grid item xs={6} marginBottom={4}>
						<MonthlyPaymentInput
							monthlyPayment={monthlyPayment}
							outstandingBalance={outstandingBalance}
							setMonthlyPayment={setMonthlyPayment}
						/>
					</Grid>

					<Grid item xs={6}>
						<RateInput rate={rate} setRate={setRate} />
					</Grid>
					<Grid item xs={6} marginBottom={4}>
						<StandardDatePicker
							label="Expected Payoff Date"
							helperText="Enter the approximate payoff date"
							onChange={handleApproxPayoffDateChange}
							value={approxPayoffDate}
							error={approxPayoffDate === ''}
						/>
					</Grid>

					<Grid item xs={6}>
						<TenureDropdown
							tenure={ownershipTenure}
							setTenure={setOwnershipTenure}
							dropDownSX={{ width: '100%' }}
							sx={{ marginBottom: 0 }}
						/>
					</Grid>

					<LoanTypeDropdown
						gridColumns={6}
						loanType={loanType}
						setLoanType={setLoanType}
					/>

					<Grid item xs={6}>
						<TextInput
							type={TEXT}
							label="Lender"
							subLabel="Enter the name of your lender"
							value={lender}
							onChange={setLender}
						/>
					</Grid>
					<Grid item xs={6} marginBottom={4}>
						<Dropdown
							dataTestTag="loanType"
							items={TYPES_OF_STUDENT_LOANS}
							selected={lenderType}
							onChange={setLenderType}
							variant="outlined"
							label="Lender Type"
						/>
					</Grid>

					<Grid item xs={6}>
						<Dropdown
							items={DEGREE_TYPES}
							label="Degree"
							selected={highestDegree}
							onChange={setHighestDegree}
							helperText="Degree obtained."
						/>
					</Grid>

					<Grid item xs={6} marginBottom={4}>
						<TextInput
							inputProps={{
								'data-test': 'school'
							}}
							type={TEXT}
							label="School attended"
							value={schoolName}
							onChange={setSchoolName}
							subLabel="Name of university"
						/>
					</Grid>

					<Grid item xs={6}>
						<TextInput
							type={TEXT}
							label="What was your field of study?"
							value={fieldOfStudy}
							onChange={setFieldOfStudy}
							subLabel="Optional"
						/>
					</Grid>
					<Grid item xs={6} marginBottom={4}>
						<TextInput
							type={NUMBER}
							label="What was your GPA?"
							value={gpa}
							onChange={handleGpachange}
							subLabel="Optional. Please report out of 4.0 scale."
						/>
					</Grid>

					<Grid item xs={6}>
						<StandardDatePicker
							helperText="Enter a year like 2022"
							inputFormat="yyyy"
							label="Graduation year"
							onChange={setGraduationYear}
							variant="outlined"
							value={graduationYear}
							views={GRADUATION_YEAR_ONLY_VIEW}
						/>
					</Grid>
					<Grid item xs={6} marginBottom={4}>
						<TextInput
							type={TEXT}
							label="Linkedin profile"
							value={linkedinProfile}
							onChange={setLinkedinProfile}
							subLabel="Optional"
						/>
					</Grid>

					<FinancialGoalDropdown
						gridColumns={6}
						withoutCashout
						financialGoal={financialGoal}
						setFinancialGoal={setFinancialGoal}
						sx={{ marginBottom: 0 }}
						dropDownLabelSX={{ whiteSpace: 'nowrap' }}
					/>

					<Grid item xs={6} />

					<Grid item xs={12} sx={{ marginTop: 4 }}>
						<LiabilityDynamicButton
							disabled={!isFormReady}
							isMutationLoading={isMutationLoading}
							onClick={submitLiabilityData}
						/>
					</Grid>
				</Grid>
			</Box>
		</>
	);
}

export default StudentLiability;
